import React, { useState, useEffect, useRef } from 'react';
import { X, Trash2 } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { useClickOutside } from '../../hooks/useClickOutside';
import { useAuth } from '../../hooks/useAuth';
import { LeaseEOIAttachment, LeaseEOIAttachmentType } from '../../types/lease-eoi';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  eoiId: number;
  attachments: LeaseEOIAttachment[];
  onAttachmentsChange: () => void;
}

const attachmentTypes: LeaseEOIAttachmentType[] = [
  'Application',
  'Supporting Document',
  'Other'
];

export default function LeaseEOIAttachmentsModal({
  isOpen,
  onClose,
  eoiId,
  attachments,
  onAttachmentsChange
}: Props) {
  const modalContentRef = useRef<HTMLDivElement>(null);
  useClickOutside(modalContentRef, onClose);

  const { user } = useAuth();
  const [newTitle, setNewTitle] = useState('');
  const [newUrl, setNewUrl] = useState('');
  const [newType, setNewType] = useState<LeaseEOIAttachmentType>(attachmentTypes[0]);
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  if (!isOpen) return null;

  const validateUrl = (url: string) => {
    try {
      new URL(url);
      return true;
    } catch {
      return false;
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    
    if (!validateUrl(newUrl)) {
      setError('Please enter a valid URL');
      return;
    }

    setIsSubmitting(true);
    
    try {
      const { error: insertError } = await supabase
        .from('lease_eoi_attachments')
        .insert({
          eoi_id: eoiId,
          title: newTitle,
          url: newUrl,
          attachment_type: newType,
          created_by: user?.id
        });

      if (insertError) throw insertError;

      setNewTitle('');
      setNewUrl('');
      setNewType(attachmentTypes[0]);
      onAttachmentsChange();
    } catch (err) {
      setError('Failed to add attachment. Please try again.');
      console.error('Error adding attachment:', err);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async (id: string) => {
    try {
      const { error: deleteError } = await supabase
        .from('lease_eoi_attachments')
        .delete()
        .eq('id', id);

      if (deleteError) throw deleteError;

      onAttachmentsChange();
    } catch (err) {
      console.error('Error deleting attachment:', err);
      setError('Failed to delete attachment. Please try again.');
    }
  };

  const getTypeColor = (type: string) => {
    switch (type) {
      case 'Application':
        return 'bg-blue-100 text-blue-800';
      case 'Supporting Document':
        return 'bg-green-100 text-green-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div 
      className="fixed inset-0 bg-black bg-opacity-50 flex items-start md:items-center justify-center p-0 z-[100] overflow-y-auto"
    >
      <div 
        ref={modalContentRef}
        className="relative bg-white w-full md:max-w-2xl md:rounded-lg md:my-8 min-h-screen md:min-h-0 max-h-full md:max-h-[90vh] flex flex-col"
      >
        {/* Header */}
        <div className="sticky top-0 z-10 flex justify-between items-center p-4 md:p-6 border-b bg-white">
          <h3 className="text-xl font-semibold text-gray-900">Lease EOI Attachments</h3>
          <button 
            onClick={onClose}
            className="rounded-full p-2 text-gray-400 hover:text-gray-500 hover:bg-gray-100"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Content - with scrollable area */}
        <div className="p-4 md:p-6 space-y-6 overflow-y-auto flex-1">
          {/* Add Attachment Form */}
          <div className="border-b pb-6">
            <h4 className="text-lg font-medium text-gray-900 mb-4">Add New Attachment</h4>
            <form id="add-attachment-form" onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-500">Title</label>
                <input
                  type="text"
                  placeholder="Enter title"
                  value={newTitle}
                  onChange={(e) => setNewTitle(e.target.value)}
                  className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 focus:border-primary focus:ring-primary"
                  required
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-500">URL</label>
                <input
                  type="text"
                  placeholder="Enter URL"
                  value={newUrl}
                  onChange={(e) => setNewUrl(e.target.value)}
                  className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 focus:border-primary focus:ring-primary"
                  required
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-500">Type</label>
                <select
                  value={newType}
                  onChange={(e) => setNewType(e.target.value as LeaseEOIAttachmentType)}
                  className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 focus:border-primary focus:ring-primary"
                >
                  {attachmentTypes.map((type) => (
                    <option key={type} value={type}>
                      {type}
                    </option>
                  ))}
                </select>
              </div>
              
              {error && <p className="text-red-500 text-sm">{error}</p>}
              
              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full px-4 py-2 text-sm font-medium text-white bg-primary hover:bg-primary-dark rounded-md disabled:opacity-50"
              >
                {isSubmitting ? 'Adding...' : 'Add Attachment'}
              </button>
            </form>
          </div>

          {/* Attachments List */}
          <div>
            <h4 className="text-lg font-medium text-gray-900 mb-4">Current Attachments</h4>
            {attachments.length === 0 ? (
              <p className="text-gray-500 text-center py-4">No attachments found</p>
            ) : (
              <ul className="divide-y divide-gray-200">
                {attachments.map((attachment) => (
                  <li key={attachment.id} className="py-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-3">
                        <span className={`px-2.5 py-0.5 rounded-full text-xs font-medium ${getTypeColor(attachment.attachment_type)}`}>
                          {attachment.attachment_type}
                        </span>
                        <div>
                          <p className="font-medium text-gray-900">{attachment.title}</p>
                          <a
                            href={attachment.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-blue-500 hover:underline text-sm"
                          >
                            View Attachment
                          </a>
                        </div>
                      </div>
                      <button
                        onClick={() => handleDelete(attachment.id)}
                        className="p-1 text-red-500 hover:text-red-700 rounded-full hover:bg-red-50"
                        title="Delete attachment"
                      >
                        <Trash2 className="h-5 w-5" />
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 z-10 flex justify-end gap-3 p-4 md:p-6 border-t bg-white">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 border border-gray-300 rounded-md"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
